import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Upload,
  FileText,
  X,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  Users,
  GitBranch,
  Loader2,
  Database,
  Merge,
  Ban,
  Download,
  Info,
} from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { importsApi, pipelineApi } from "../../lib/api";

const selectCls =
  "w-full h-9 px-2.5 rounded-md border border-slate-200 bg-white text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400";

const STEPS = [
  { n: 1, label: "Upload" },
  { n: 2, label: "Map Fields" },
  { n: 3, label: "Review" },
  { n: 4, label: "Done" },
];

const matchColor = {
  name: "bg-violet-100 text-violet-700",
  email: "bg-blue-100 text-blue-700",
  phone: "bg-emerald-100 text-emerald-700",
  address: "bg-amber-100 text-amber-700",
};

const firstSample = (sampleRows, col) => {
  if (!col) return "";
  for (const r of sampleRows || []) {
    const v = (r[col] || "").toString().trim();
    if (v) return v;
  }
  return "";
};

export default function ImportWizard({ open, onClose, onImported }) {
  const [step, setStep] = useState(1);
  const [entity, setEntity] = useState("customers");

  // leads target
  const [pipelines, setPipelines] = useState([]);
  const [pipelineId, setPipelineId] = useState("");
  const [stageId, setStageId] = useState("");
  const [creatingPipeline, setCreatingPipeline] = useState(false);

  // upload / parse
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null); // {importId, columns, rowCount, sampleRows, suggestedMapping, fields}
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  // mapping
  const [mapping, setMapping] = useState({});

  // analyze
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null); // {summary, rows, entity}
  const [decisions, setDecisions] = useState({});
  const [defaultAction, setDefaultAction] = useState("skip");

  // commit
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = useCallback(() => {
    setStep(1);
    setEntity("customers");
    setPipelineId("");
    setStageId("");
    setFile(null);
    setParsed(null);
    setMapping({});
    setAnalysis(null);
    setDecisions({});
    setDefaultAction("skip");
    setResult(null);
  }, []);

  // Clear any lingering bottom-right toasts (e.g. the one-time task reminder)
  // so they can't overlap / intercept clicks on the wizard's action bar.
  useEffect(() => {
    if (open) toast.dismiss();
  }, [open]);

  // Load pipelines when leads is selected
  useEffect(() => {
    if (!open || entity !== "leads") return;
    importsApi
      .fields("leads")
      .then((d) => {
        const ps = d.pipelines || [];
        setPipelines(ps);
        if (ps.length && !pipelineId) {
          setPipelineId(ps[0].id);
          setStageId(ps[0].stages?.[0]?.id || "");
        }
      })
      .catch(() => toast.error("Could not load pipelines"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entity]);

  const stages = useMemo(
    () => pipelines.find((p) => p.id === pipelineId)?.stages || [],
    [pipelines, pipelineId],
  );

  const closeAndReset = useCallback(() => {
    if (parsed?.importId && !result) {
      importsApi.cancel(parsed.importId).catch(() => {});
    }
    reset();
    onClose && onClose();
  }, [parsed, result, reset, onClose]);

  const handleFile = async (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please choose a .csv file");
      return;
    }
    setFile(f);
    setParsing(true);
    try {
      const res = await importsApi.parse(f, entity);
      setParsed(res);
      setMapping(res.suggestedMapping || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read that CSV");
      setFile(null);
    } finally {
      setParsing(false);
    }
  };

  const createLeadsPipeline = async () => {
    setCreatingPipeline(true);
    try {
      const p = await pipelineApi.createPipeline({ name: "Imported Leads", color: "#2563EB" });
      const refreshed = await importsApi.fields("leads");
      setPipelines(refreshed.pipelines || []);
      setPipelineId(p.id);
      const ps = (refreshed.pipelines || []).find((x) => x.id === p.id);
      setStageId(ps?.stages?.[0]?.id || "");
      toast.success("Created 'Imported Leads' pipeline");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create pipeline");
    } finally {
      setCreatingPipeline(false);
    }
  };

  // ---- mapping helpers ----
  const requiredFields = (parsed?.fields || []).filter((f) => f.required);
  const optionalFields = (parsed?.fields || []).filter((f) => !f.required);

  const requiredSatisfied = useMemo(() => {
    if (!parsed) return false;
    return requiredFields.every((f) => {
      if (mapping[f.key]) return true;
      if (f.key === "name" && (mapping.firstName || mapping.lastName)) return true;
      return false;
    });
  }, [parsed, requiredFields, mapping]);

  const setFieldMap = (key, col) => {
    setMapping((prev) => {
      const next = { ...prev };
      if (col) next[key] = col;
      else delete next[key];
      return next;
    });
  };

  // ---- step transitions ----
  const goAnalyze = async () => {
    setAnalyzing(true);
    setStep(3);
    try {
      const payload = { importId: parsed.importId, mapping };
      if (entity === "leads") payload.pipelineId = pipelineId;
      const res = await importsApi.analyze(payload);
      setAnalysis(res);
      setDecisions({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Validation failed");
      setStep(2);
    } finally {
      setAnalyzing(false);
    }
  };

  const runImport = async () => {
    setCommitting(true);
    setStep(4);
    try {
      const payload = {
        importId: parsed.importId,
        mapping,
        decisions,
        defaultDuplicateAction: defaultAction,
        mergeStrategy: "fill",
      };
      if (entity === "leads") {
        payload.pipelineId = pipelineId;
        payload.stageId = stageId;
      }
      const res = await importsApi.commit(payload);
      setResult(res);
      onImported && onImported(res);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
      setStep(3);
    } finally {
      setCommitting(false);
    }
  };

  const dupRows = (analysis?.rows || []).filter((r) => r.status === "duplicate");
  const invalidRows = (analysis?.rows || []).filter((r) => r.status === "invalid");
  const newRows = (analysis?.rows || []).filter((r) => r.status === "new");

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[130] flex flex-col bg-slate-50" data-testid="import-wizard">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3.5 bg-white border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center text-white">
            <Database className="h-5 w-5" />
          </div>
          <div>
            <div className="text-[15px] font-bold text-slate-900">Import {entity === "leads" ? "Leads" : "Customers"}</div>
            <div className="text-[12px] text-slate-500">Upload a CSV, map your columns, resolve duplicates, and import.</div>
          </div>
        </div>
        <button
          onClick={closeAndReset}
          data-testid="import-close"
          className="h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Stepper */}
      <div className="flex items-center justify-center gap-2 sm:gap-4 px-6 py-3 bg-white border-b border-slate-100">
        {STEPS.map((s, i) => {
          const active = step === s.n;
          const done = step > s.n;
          return (
            <div key={s.n} className="flex items-center gap-2 sm:gap-4">
              <div className="flex items-center gap-2" data-testid={`import-step-${s.n}`}>
                <div
                  className={`h-7 w-7 rounded-full flex items-center justify-center text-[12px] font-bold ${
                    done ? "bg-emerald-600 text-white" : active ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-500"
                  }`}
                >
                  {done ? <CheckCircle2 className="h-4 w-4" /> : s.n}
                </div>
                <span className={`text-[13px] font-semibold ${active ? "text-slate-900" : "text-slate-400"}`}>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div className="w-6 sm:w-10 h-px bg-slate-200" />}
            </div>
          );
        })}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5">
        <div className="max-w-4xl mx-auto">
          {step === 1 && (
            <StepUpload
              entity={entity}
              setEntity={(e) => {
                setEntity(e);
                setFile(null);
                setParsed(null);
                setMapping({});
              }}
              pipelines={pipelines}
              pipelineId={pipelineId}
              setPipelineId={(id) => {
                setPipelineId(id);
                const ps = pipelines.find((p) => p.id === id);
                setStageId(ps?.stages?.[0]?.id || "");
              }}
              stageId={stageId}
              setStageId={setStageId}
              stages={stages}
              creatingPipeline={creatingPipeline}
              onCreatePipeline={createLeadsPipeline}
              file={file}
              parsing={parsing}
              parsed={parsed}
              dragOver={dragOver}
              setDragOver={setDragOver}
              fileRef={fileRef}
              handleFile={handleFile}
            />
          )}

          {step === 2 && parsed && (
            <StepMap
              parsed={parsed}
              mapping={mapping}
              setFieldMap={setFieldMap}
              setMapping={setMapping}
              requiredFields={requiredFields}
              optionalFields={optionalFields}
              requiredSatisfied={requiredSatisfied}
            />
          )}

          {step === 3 && (
            <StepReview
              analyzing={analyzing}
              analysis={analysis}
              entity={entity}
              defaultAction={defaultAction}
              setDefaultAction={setDefaultAction}
              decisions={decisions}
              setDecisions={setDecisions}
              dupRows={dupRows}
              invalidRows={invalidRows}
              newRows={newRows}
            />
          )}

          {step === 4 && (
            <StepResult committing={committing} result={result} entity={entity} />
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-6 py-3.5 bg-white border-t border-slate-200">
        <Button variant="outline" onClick={closeAndReset} data-testid="import-cancel" className="h-9 text-[13px]">
          {step === 4 && result ? "Close" : "Cancel"}
        </Button>
        <div className="flex items-center gap-2">
          {step > 1 && step < 4 && (
            <Button
              variant="outline"
              onClick={() => setStep((s) => s - 1)}
              data-testid="import-back"
              className="h-9 text-[13px]"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
            </Button>
          )}
          {step === 1 && (
            <Button
              onClick={() => setStep(2)}
              disabled={!parsed || (entity === "leads" && (!pipelineId || !stageId))}
              data-testid="import-next-1"
              className="h-9 bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-semibold disabled:bg-slate-200 disabled:text-slate-400"
            >
              Next <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          )}
          {step === 2 && (
            <Button
              onClick={goAnalyze}
              disabled={!requiredSatisfied}
              data-testid="import-next-2"
              className="h-9 bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-semibold disabled:bg-slate-200 disabled:text-slate-400"
            >
              Review <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          )}
          {step === 3 && (
            <Button
              onClick={runImport}
              disabled={analyzing || !analysis || (analysis.summary.total - analysis.summary.invalid) === 0}
              data-testid="import-run"
              className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-[13px] font-semibold disabled:bg-slate-200 disabled:text-slate-400"
            >
              Import {analysis ? analysis.summary.total - analysis.summary.invalid : ""} records
            </Button>
          )}
          {step === 4 && result && (
            <Button
              onClick={closeAndReset}
              data-testid="import-done"
              className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-[13px] font-semibold"
            >
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Done
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Step 1
function StepUpload({
  entity, setEntity, pipelines, pipelineId, setPipelineId, stageId, setStageId, stages,
  creatingPipeline, onCreatePipeline, file, parsing, parsed, dragOver, setDragOver, fileRef, handleFile,
}) {
  return (
    <div className="space-y-5">
      <div>
        <div className="text-[12px] uppercase tracking-wider font-semibold text-slate-500 mb-2">What are you importing?</div>
        <div className="grid grid-cols-2 gap-3 max-w-md">
          {[
            { k: "customers", label: "Customers", icon: Users, sub: "Into your customer database" },
            { k: "leads", label: "Leads", icon: GitBranch, sub: "Into a sales pipeline" },
          ].map((o) => {
            const active = entity === o.k;
            const Icon = o.icon;
            return (
              <button
                key={o.k}
                onClick={() => setEntity(o.k)}
                data-testid={`import-entity-${o.k}`}
                className={`text-left p-3.5 rounded-xl border-2 transition-colors ${
                  active ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${active ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="text-[14px] font-bold text-slate-900">{o.label}</div>
                </div>
                <div className="text-[12px] text-slate-500 mt-1.5">{o.sub}</div>
              </button>
            );
          })}
        </div>
      </div>

      {entity === "leads" && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-[12px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Where should leads go?</div>
          {pipelines.length === 0 ? (
            <div className="flex items-center gap-3">
              <div className="text-[13px] text-slate-600">You don't have a pipeline yet.</div>
              <Button onClick={onCreatePipeline} disabled={creatingPipeline} data-testid="import-create-pipeline" className="h-8 text-[12.5px] bg-blue-600 hover:bg-blue-700 text-white">
                {creatingPipeline ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
                Create "Imported Leads" pipeline
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 max-w-lg">
              <div>
                <label className="block text-[12px] font-semibold text-slate-600 mb-1">Pipeline</label>
                <select value={pipelineId} onChange={(e) => setPipelineId(e.target.value)} data-testid="import-pipeline-select" className={selectCls}>
                  {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-slate-600 mb-1">Stage</label>
                <select value={stageId} onChange={(e) => setStageId(e.target.value)} data-testid="import-stage-select" className={selectCls}>
                  {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      <div>
        <div className="text-[12px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Upload your CSV file</div>
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
          onClick={() => fileRef.current?.click()}
          data-testid="import-dropzone"
          className={`rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${
            dragOver ? "border-blue-500 bg-blue-50" : "border-slate-300 bg-white hover:border-blue-400 hover:bg-slate-50"
          }`}
        >
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" data-testid="import-file-input"
            onChange={(e) => handleFile(e.target.files?.[0])} />
          {parsing ? (
            <div className="flex flex-col items-center gap-2 text-slate-500">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <div className="text-[13px] font-medium">Reading {file?.name}…</div>
            </div>
          ) : parsed ? (
            <div className="flex flex-col items-center gap-2">
              <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <FileText className="h-6 w-6 text-emerald-600" />
              </div>
              <div className="text-[14px] font-bold text-slate-900">{parsed.filename}</div>
              <div className="text-[13px] text-slate-500">
                <span className="font-semibold text-emerald-700">{parsed.rowCount.toLocaleString()}</span> rows ·{" "}
                <span className="font-semibold">{parsed.columns.length}</span> columns detected
              </div>
              <div className="text-[12px] text-blue-600 font-medium mt-1">Click to choose a different file</div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 text-slate-500">
              <div className="h-12 w-12 rounded-xl bg-slate-100 flex items-center justify-center">
                <Upload className="h-6 w-6 text-slate-400" />
              </div>
              <div className="text-[14px] font-semibold text-slate-700">Drag & drop your CSV here, or click to browse</div>
              <div className="text-[12px] text-slate-400">First row must be column headers · up to 5,000 rows</div>
            </div>
          )}
        </div>

        {/* Example template — shows every recognized column with 2 sample rows and
            a one-click download so admins know exactly what to send. Only rendered
            before a file is picked so it doesn't clutter the post-parse state. */}
        {!parsed && !parsing && <SampleTemplate entity={entity} />}
      </div>
    </div>
  );
}

// ------------------------------------------------------------ Sample template
// A compact "here's what a good CSV looks like" preview shown on the Upload
// step. Column headers match the field labels our backend auto-maps, and the
// sample rows use generic data so admins can eyeball the format at a glance.
const CUSTOMER_TEMPLATE = {
  columns: [
    "Full Name", "Customer Type", "Primary Phone", "Alternate Phone", "Email", "Alternate Email",
    "Street Address", "City", "State", "Zip",
    "Membership Tier", "Membership Start Date", "Membership Expires",
    "Customer Since", "Last Service Date",
    "Lifetime Value", "Open Balance", "Rating",
    "Tags", "Initial Note",
  ],
  rows: [
    [
      "Customer A", "Residential", "(555) 010-1001", "", "customer.a@example.com", "",
      "123 Example St", "Springfield", "IL", "62701",
      "Gold", "2024-03-15", "2027-03-15",
      "2022-06-01", "2026-05-14",
      "3250.00", "0", "5",
      "vip, referral", "Prefers morning appointments",
    ],
    [
      "Customer B, LLC", "Commercial", "(555) 010-2002", "(555) 010-2003", "billing@customer-b.example", "ap@customer-b.example",
      "456 Sample Blvd, Suite 200", "Springfield", "IL", "62704",
      "Platinum", "2025-01-10", "2026-01-10",
      "2019-11-20", "2026-06-02",
      "18750.50", "425.00", "4",
      "commercial, quarterly-service", "Net-30 billing; PO required over $1,000",
    ],
  ],
};
const LEAD_TEMPLATE = {
  columns: ["Lead Name", "Email", "Phone", "Estimated Value", "Lead Source", "Tags", "Notes"],
  rows: [
    ["Prospect A", "a@example.com", "(555) 020-1001", "2500", "Website", "hot, new-build", "Wants estimate for HVAC swap"],
    ["Prospect B, LLC", "billing@prospect-b.example", "(555) 020-2002", "12000", "Referral", "commercial", "Multi-unit maintenance contract"],
  ],
};

function csvEscape(v) {
  const s = String(v ?? "");
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function SampleTemplate({ entity }) {
  const tpl = entity === "leads" ? LEAD_TEMPLATE : CUSTOMER_TEMPLATE;
  const filename = entity === "leads"
    ? "serviceguru-leads-template.csv"
    : "serviceguru-customers-template.csv";

  const download = () => {
    const lines = [
      tpl.columns.map(csvEscape).join(","),
      ...tpl.rows.map((r) => r.map(csvEscape).join(",")),
    ].join("\n");
    // Excel-friendly BOM so accented characters survive round-trips.
    const blob = new Blob(["\ufeff" + lines], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="import-sample-template">
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-7 w-7 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
            <FileText className="h-3.5 w-3.5 text-blue-600" />
          </div>
          <div className="min-w-0">
            <div className="text-[13px] font-bold text-slate-900">Example template</div>
            <div className="text-[11.5px] text-slate-500 truncate">
              {tpl.columns.length} columns · {tpl.rows.length} sample rows — headers must be on the first row
            </div>
          </div>
        </div>
        <Button
          onClick={download}
          data-testid="import-sample-template-download"
          className="h-8 px-3 bg-blue-600 hover:bg-blue-700 text-white text-[12.5px]"
        >
          <Download className="h-3.5 w-3.5 mr-1.5" />
          Download example CSV
        </Button>
      </div>
      <div className="overflow-x-auto" data-testid="import-sample-template-scroll">
        <table className="min-w-max text-[12px] text-slate-700">
          <thead className="bg-slate-50 border-b border-slate-200 text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">
            <tr>
              {tpl.columns.map((c) => (
                <th key={c} className="px-3 py-2 whitespace-nowrap text-left">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tpl.rows.map((row, ri) => (
              <tr key={ri} className={ri % 2 === 0 ? "bg-white" : "bg-slate-50/40"}>
                {row.map((cell, ci) => (
                  <td key={ci} className="px-3 py-2 whitespace-nowrap border-b border-slate-100">
                    {cell === "" ? <span className="text-slate-300">—</span> : cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {entity === "customers" && (
        <div className="px-4 py-2.5 border-t border-slate-100 flex items-start gap-2 text-[11.5px] text-slate-500 bg-slate-50/60">
          <Info className="h-3.5 w-3.5 mt-0.5 text-slate-400 shrink-0" />
          <span>
            Any column names close to ours (e.g. <code className="px-1 py-0.5 rounded bg-white border border-slate-200 text-slate-600">phone_number</code>,
            <code className="px-1 py-0.5 rounded bg-white border border-slate-200 text-slate-600 ml-1">zipcode</code>) auto-map on the next step —
            you&apos;ll still be able to review and adjust. <strong className="text-slate-700">Full Name</strong> is the only required column;
            everything else is optional. Job history, invoices and estimates are built automatically as those records are created — they aren&apos;t
            part of the customer CSV.
          </span>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ Step 2
function StepMap({ parsed, mapping, setFieldMap, setMapping, requiredFields, optionalFields, requiredSatisfied }) {
  const columns = parsed.columns || [];
  const sampleRows = parsed.sampleRows || [];

  const FieldRow = ({ f }) => {
    const col = mapping[f.key] || "";
    const sample = firstSample(sampleRows, col);
    const unmappedRequired = f.required && !col && !(f.key === "name" && (mapping.firstName || mapping.lastName));
    return (
      <div className="grid grid-cols-12 items-center gap-3 px-3 py-2.5 border-b border-slate-100 last:border-0">
        <div className="col-span-4 flex items-center gap-2">
          {unmappedRequired && <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />}
          <span className="text-[13px] font-semibold text-slate-800">{f.label}</span>
          {f.required && <span className="text-rose-500 text-[13px]">*</span>}
        </div>
        <div className="col-span-4">
          <select
            value={col}
            onChange={(e) => setFieldMap(f.key, e.target.value)}
            data-testid={`import-map-${f.key}`}
            className={`${selectCls} ${unmappedRequired ? "border-amber-300 bg-amber-50" : ""}`}
          >
            <option value="">— Not mapped —</option>
            {columns.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="col-span-4 text-[12px] text-slate-400 truncate">
          {sample ? <span className="text-slate-500">e.g. <span className="font-medium text-slate-700">{sample}</span></span> : "—"}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <p className="text-[13px] text-slate-600">
        Match each ServiceGuru Pro field to a column from your file. We've auto-matched what we could — please review,
        especially the required fields.
      </p>

      {!requiredSatisfied && (
        <div className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3.5 py-2.5" data-testid="import-map-warning">
          <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
          <span className="text-[13px] text-amber-800 font-medium">Map the required field(s) before continuing.</span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-[12px] text-slate-400">{Object.keys(mapping).length} of {parsed.fields.length} fields mapped</div>
        <button
          onClick={() => setMapping(parsed.suggestedMapping || {})}
          data-testid="import-reset-mapping"
          className="text-[12.5px] font-semibold text-blue-600 hover:underline"
        >
          Reset to suggested
        </button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-3 py-2 bg-blue-50/60 border-b border-slate-100 grid grid-cols-12 gap-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">
          <div className="col-span-4">ServiceGuru Field</div>
          <div className="col-span-4">CSV Column</div>
          <div className="col-span-4">Sample</div>
        </div>
        <div className="px-3 py-1.5 bg-rose-50/40 text-[11px] uppercase tracking-wider font-bold text-rose-600">Required</div>
        {requiredFields.map((f) => <FieldRow key={f.key} f={f} />)}
        <div className="px-3 py-1.5 bg-slate-50 text-[11px] uppercase tracking-wider font-bold text-slate-500">Optional</div>
        {optionalFields.map((f) => <FieldRow key={f.key} f={f} />)}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Step 3
function MatchBadges({ matchedOn }) {
  return (
    <div className="flex flex-wrap gap-1">
      {matchedOn.map((m) => (
        <span key={m} className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${matchColor[m] || "bg-slate-100 text-slate-600"}`}>
          {m}
        </span>
      ))}
    </div>
  );
}

function StepReview({ analyzing, analysis, entity, defaultAction, setDefaultAction, decisions, setDecisions, dupRows, invalidRows, newRows }) {
  if (analyzing || !analysis) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-20 text-slate-500">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <div className="text-[14px] font-medium">Validating rows and checking for duplicates…</div>
      </div>
    );
  }
  const s = analysis.summary;
  const cards = [
    { label: "Total rows", value: s.total, color: "text-slate-900" },
    { label: "New", value: s.new, color: "text-emerald-600" },
    { label: "Duplicates", value: s.duplicate, color: "text-amber-600" },
    { label: "Invalid", value: s.invalid, color: "text-rose-600" },
  ];

  const fieldLabel = (r) => r.data.name || "(no name)";

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-3.5" data-testid={`import-summary-${c.label.split(" ")[0].toLowerCase()}`}>
            <div className={`text-[24px] font-bold ${c.color}`}>{c.value.toLocaleString()}</div>
            <div className="text-[12px] text-slate-500">{c.label}</div>
          </div>
        ))}
      </div>

      {s.duplicate > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4 space-y-3" data-testid="import-duplicates-panel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-[13px] font-bold text-slate-900">{s.duplicate} possible duplicate{s.duplicate > 1 ? "s" : ""} found</div>
            <div className="flex items-center gap-2">
              <span className="text-[12px] text-slate-500">Default action for all:</span>
              <select value={defaultAction} onChange={(e) => setDefaultAction(e.target.value)} data-testid="import-default-action" className={`${selectCls} w-44 h-8`}>
                <option value="skip">Skip (don't import)</option>
                <option value="merge">Merge (fill empty fields)</option>
                <option value="create">Create as new record</option>
              </select>
            </div>
          </div>
          <p className="text-[12px] text-slate-500">
            Merging only fills empty fields on the existing record — it never overwrites your current data. Set a per-row action below to override the default.
          </p>
          <div className="rounded-lg border border-slate-200 bg-white max-h-72 overflow-y-auto divide-y divide-slate-100">
            {dupRows.map((r) => (
              <div key={r.i} className="grid grid-cols-12 items-center gap-2 px-3 py-2" data-testid={`import-dup-row-${r.i}`}>
                <div className="col-span-4 min-w-0">
                  <div className="text-[13px] font-semibold text-slate-800 truncate">{fieldLabel(r)}</div>
                  <div className="text-[11px] text-slate-400 truncate">{r.data.email || r.data.phone || ""}</div>
                </div>
                <div className="col-span-5 min-w-0">
                  {r.matches.slice(0, 1).map((m) => (
                    <div key={m.id} className="flex items-center gap-2">
                      <span className="text-[12px] text-slate-500 truncate">matches <span className="font-semibold text-slate-700">{m.label}</span></span>
                      <MatchBadges matchedOn={m.matchedOn} />
                    </div>
                  ))}
                  {r.matches.length > 1 && <div className="text-[11px] text-slate-400">+{r.matches.length - 1} more</div>}
                </div>
                <div className="col-span-3">
                  <select
                    value={decisions[r.i] || defaultAction}
                    onChange={(e) => setDecisions((p) => ({ ...p, [r.i]: e.target.value }))}
                    data-testid={`import-dup-action-${r.i}`}
                    className={`${selectCls} h-8 text-[12px]`}
                  >
                    <option value="skip">Skip</option>
                    <option value="merge">Merge</option>
                    <option value="create">Create new</option>
                  </select>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {s.invalid > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-4 space-y-2" data-testid="import-invalid-panel">
          <div className="text-[13px] font-bold text-slate-900">{s.invalid} row{s.invalid > 1 ? "s" : ""} can't be imported</div>
          <div className="rounded-lg border border-slate-200 bg-white max-h-48 overflow-y-auto divide-y divide-slate-100">
            {invalidRows.slice(0, 50).map((r) => (
              <div key={r.i} className="flex items-center justify-between px-3 py-2">
                <span className="text-[12.5px] text-slate-600">Row {r.i + 1}</span>
                <span className="text-[12px] text-rose-600 font-medium">{r.errors.join(", ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {s.new > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2" data-testid="import-new-panel">
          <div className="text-[13px] font-bold text-slate-900">{s.new} new {entity === "leads" ? "lead" : "customer"}{s.new > 1 ? "s" : ""} ready to import</div>
          <div className="rounded-lg border border-slate-200 max-h-56 overflow-y-auto divide-y divide-slate-100">
            {newRows.slice(0, 25).map((r) => (
              <div key={r.i} className="flex items-center justify-between px-3 py-1.5">
                <span className="text-[12.5px] font-medium text-slate-700 truncate">{fieldLabel(r)}</span>
                <span className="text-[11.5px] text-slate-400 truncate ml-3">{r.data.email || r.data.phone || ""}</span>
              </div>
            ))}
            {s.new > 25 && <div className="px-3 py-1.5 text-[11.5px] text-slate-400">+{s.new - 25} more…</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ Step 4
function StepResult({ committing, result, entity }) {
  if (committing || !result) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-20 text-slate-500">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        <div className="text-[14px] font-medium">Importing your records…</div>
      </div>
    );
  }
  const cards = [
    { label: "Created", value: result.created, icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Merged", value: result.merged, icon: Merge, color: "text-blue-600", bg: "bg-blue-50" },
    { label: "Skipped", value: result.skipped, icon: Ban, color: "text-slate-500", bg: "bg-slate-50" },
    { label: "Failed", value: result.failed, icon: AlertTriangle, color: "text-rose-600", bg: "bg-rose-50" },
  ];
  return (
    <div className="space-y-5">
      <div className="flex flex-col items-center text-center gap-2 py-4">
        <div className="h-14 w-14 rounded-2xl bg-emerald-100 flex items-center justify-center">
          <CheckCircle2 className="h-8 w-8 text-emerald-600" />
        </div>
        <div className="text-[18px] font-bold text-slate-900">Import complete</div>
        <div className="text-[13px] text-slate-500">
          Your {entity === "leads" ? "leads" : "customers"} have been imported into ServiceGuru Pro.
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className={`rounded-xl border border-slate-200 p-3.5 ${c.bg}`} data-testid={`import-result-${c.label.toLowerCase()}`}>
              <Icon className={`h-5 w-5 ${c.color} mb-1.5`} />
              <div className={`text-[24px] font-bold ${c.color}`}>{c.value.toLocaleString()}</div>
              <div className="text-[12px] text-slate-500">{c.label}</div>
            </div>
          );
        })}
      </div>
      {result.errors?.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-4">
          <div className="text-[13px] font-bold text-slate-900 mb-2">{result.failed} row{result.failed > 1 ? "s" : ""} could not be imported</div>
          <div className="rounded-lg border border-slate-200 bg-white max-h-48 overflow-y-auto divide-y divide-slate-100">
            {result.errors.map((e, idx) => (
              <div key={idx} className="flex items-center justify-between px-3 py-2">
                <span className="text-[12.5px] text-slate-600">Row {e.row}</span>
                <span className="text-[12px] text-rose-600 font-medium">{e.error}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
